// Frontend/src/lib/sseFrame.ts
//
// Parsing one SSE frame (everything between two blank lines), split out of
// notificationStream.ts so it's testable without a real ReadableStream --
// see sseFrame.test.ts.

import type { StreamNotification } from "./notificationStream";

export type ParsedSseFrame =
  | { type: "heartbeat" }
  | { type: "cycle-end"; id: string | null }
  | { type: "open"; id: string | null }
  | { type: "notification"; id: string | null; data: StreamNotification }
  | { type: "unknown"; id: string | null };

/**
 * `frame` is one event's worth of lines, already split from the stream on
 * the blank-line boundary (`\n\n`) -- this function never sees that
 * boundary itself.
 */
export function parseSseFrame(frame: string): ParsedSseFrame {
  if (!frame || frame.startsWith(":")) return { type: "heartbeat" };

  let event = "message";
  const dataLines: string[] = [];
  let id: string | null = null;

  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    else if (line.startsWith("id:")) id = line.slice(3).trim();
  }

  if (event === "cycle-end") return { type: "cycle-end", id };
  if (event === "open") return { type: "open", id };

  if (event === "notification" && dataLines.length > 0) {
    try {
      return { type: "notification", id, data: JSON.parse(dataLines.join("\n")) };
    } catch {
      return { type: "unknown", id };
    }
  }

  return { type: "unknown", id };
}
